import { ReactElement, useId } from 'react'
import { NumberField as BaseNumberField } from '@base-ui/react/number-field'
import {
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  type SxProps,
  type Theme,
} from '@mui/material'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

type NumberFieldProps = {
  id?: string
  label?: ReactElement | string
  size?: 'small' | 'medium'
  error?: boolean
  helperText?: string
  fullWidth?: boolean
  value?: number | null
  defaultValue?: number
  min?: number
  max?: number
  step?: number | 'any'
  required?: boolean
  disabled?: boolean
  onValueChange?: BaseNumberField.Root.Props['onValueChange']
  sx?: SxProps<Theme>
}

export default function NumberField({
  id: idProp,
  label,
  error,
  helperText,
  fullWidth,
  size = 'medium',
  value,
  defaultValue,
  min,
  max,
  step,
  required,
  disabled,
  onValueChange,
  sx,
}: NumberFieldProps): ReactElement {
  let generatedId = useId()
  let id = idProp ?? generatedId
  let shrink =
    (value !== null && value !== undefined) || defaultValue !== undefined

  return (
    <BaseNumberField.Root
      value={value}
      defaultValue={defaultValue}
      min={min}
      max={max}
      step={step}
      required={required}
      disabled={disabled}
      onValueChange={onValueChange}
      render={(props, state) => (
        <FormControl
          size={size}
          ref={props.ref}
          disabled={state.disabled}
          required={state.required}
          error={error}
          fullWidth={fullWidth}
          variant="outlined"
          sx={sx}
        >
          {props.children}
        </FormControl>
      )}
    >
      <InputLabel htmlFor={id} shrink={shrink}>
        {label}
      </InputLabel>
      <BaseNumberField.Input
        id={id}
        render={(props, state) => (
          <OutlinedInput
            aria-describedby={`${id}-helper-text`}
            label={label}
            inputRef={props.ref}
            value={state.inputValue}
            onBlur={props.onBlur}
            onChange={props.onChange}
            onKeyUp={props.onKeyUp}
            onKeyDown={props.onKeyDown}
            onFocus={props.onFocus}
            slotProps={{ input: props }}
            endAdornment={
              <InputAdornment
                position="end"
                sx={{
                  flexDirection: 'column',
                  maxHeight: 'unset',
                  alignSelf: 'stretch',
                  borderLeft: '1px solid',
                  borderColor: 'divider',
                  ml: 0,
                  '& button': {
                    py: 0,
                    flex: 1,
                    borderRadius: 0.5,
                  },
                }}
              >
                <BaseNumberField.Increment
                  render={(incrementProps, incrementState) => (
                    <IconButton
                      onClick={incrementProps.onClick}
                      onPointerDown={incrementProps.onPointerDown}
                      disabled={incrementState.disabled}
                      tabIndex={incrementProps.tabIndex}
                      size={size}
                      aria-label="Increase"
                    >
                      {incrementProps.children}
                    </IconButton>
                  )}
                >
                  <KeyboardArrowUpIcon
                    fontSize={size}
                    sx={{ transform: 'translateY(2px)' }}
                  />
                </BaseNumberField.Increment>
                <BaseNumberField.Decrement
                  render={(decrementProps, decrementState) => (
                    <IconButton
                      onClick={decrementProps.onClick}
                      onPointerDown={decrementProps.onPointerDown}
                      disabled={decrementState.disabled}
                      tabIndex={decrementProps.tabIndex}
                      size={size}
                      aria-label="Decrease"
                    >
                      {decrementProps.children}
                    </IconButton>
                  )}
                >
                  <KeyboardArrowDownIcon
                    fontSize={size}
                    sx={{ transform: 'translateY(-2px)' }}
                  />
                </BaseNumberField.Decrement>
              </InputAdornment>
            }
            sx={{ pr: 0 }}
          />
        )}
      />
      <FormHelperText
        id={`${id}-helper-text`}
        sx={{ ml: 0, '&:empty': { mt: 0 } }}
      >
        {helperText}
      </FormHelperText>
    </BaseNumberField.Root>
  )
}
